import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface BestPracticeRow {
  readonly bestPracticeId: string;
  readonly practiceCode: string;
  readonly practiceName: string;
  readonly businessArea: string;
  readonly approvalStatus: string;
  readonly status: string;
}

const STATUSES = ['', 'DRAFT', 'IN_REVIEW', 'APPROVED', 'PUBLISHED', 'REJECTED', 'ARCHIVED', 'RETIRED', 'OBSOLETE'];
const APPROVAL = ['', 'PENDING', 'APPROVED', 'REJECTED'];

/**
 * M35 — Best Practice Library (§33). Curated register of organisational best
 * practices (T467) across business areas, carrying the §16 approval status
 * (PENDING → APPROVED) and the §8 lifecycle (only APPROVED practices publish).
 * Filterable by lifecycle status, approval status, business area and free-text
 * search. Tenant isolation is enforced server-side (LAW 4).
 */
export function BestPracticeLibrary(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<BestPracticeRow[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('');
  const [approvalStatus, setApprovalStatus] = useState('');
  const [businessArea, setBusinessArea] = useState('');
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [newName, setNewName] = useState('');
  const [newArea, setNewArea] = useState('');
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (approvalStatus) params.set('approvalStatus', approvalStatus);
    if (businessArea) params.set('businessArea', businessArea);
    if (q) params.set('q', q);
    const qs = params.toString();
    apiRequest<{ data: BestPracticeRow[]; total: number }>(`/v1/kms/best-practices${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, status, approvalStatus, businessArea, q]);

  useEffect(() => load(), [load]);

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newName.trim()) { setActionError(t('m35.bp.nameRequired')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/kms/best-practices`, {
        method: 'POST',
        body: { practiceName: newName.trim(), businessArea: newArea.trim() || undefined },
        token,
      });
      setNotice(t('m35.common.created', { name: newName.trim() }));
      setNewName('');
      setNewArea('');
      load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m35.bp.notAuthorisedManage'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const act = async (r: BestPracticeRow, op: string, label: string, perm: string, body?: unknown): Promise<void> => {
    setActionError(null);
    setNotice(null);
    setBusy(true);
    try {
      await apiRequest(`/v1/kms/best-practices/${r.bestPracticeId}/${op}`, { method: 'POST', body, token });
      setNotice(t('m35.common.notice', { code: r.practiceCode, label }));
      load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m35.common.notAuthorisedAction', { perm }));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const remove = async (r: BestPracticeRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    setBusy(true);
    try {
      await apiRequest(`/v1/kms/best-practices/${r.bestPracticeId}`, { method: 'DELETE', token });
      setNotice(t('m35.common.notice', { code: r.practiceCode, label: t('m35.common.act.deleted') }));
      load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m35.bp.notAuthorisedManage'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <main aria-label="best-practice-library">
      <h1>{t('m35.bp.title')}</h1>

      <form aria-label="best-practice-form" onSubmit={(e) => void create(e)}>
        <label>
          {t('m35.bp.practiceName')}
          <input aria-label="field-practice-name" value={newName} onChange={(e) => setNewName(e.target.value)} />
        </label>
        <label>
          {t('m35.bp.businessArea')}
          <input aria-label="field-practice-area" value={newArea} onChange={(e) => setNewArea(e.target.value)} />
        </label>
        <Button variant="primary" type="submit" aria-label="submit-practice" disabled={busy}>{busy ? t('m35.common.saving') : t('m35.bp.createBtn')}</Button>
      </form>
      {actionError && <p role="alert" aria-label="best-practice-action-error">{actionError}</p>}
      {notice && <p aria-label="best-practice-action-notice">{notice}</p>}

      <div className="filters">
        <label>
          {t('m35.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m35.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m35.bp.approval')}
          <select aria-label="filter-approval" value={approvalStatus} onChange={(e) => setApprovalStatus(e.target.value)}>
            {APPROVAL.map((s) => <option key={s || 'all'} value={s}>{s || t('m35.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m35.bp.businessArea')}
          <input aria-label="filter-businessarea" value={businessArea} onChange={(e) => setBusinessArea(e.target.value)} placeholder={t('m35.bp.businessArea')} />
        </label>
        <label>
          {t('m35.common.search')}
          <input aria-label="filter-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('m35.bp.searchPh')} />
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m35.common.loading')}</p> : (
        <>
          <p aria-label="best-practice-count">{t('m35.bp.count', { n: total })}</p>
          <DataTable
            ariaLabel="best-practice-table"
            rows={rows}
            rowKey={(r) => r.bestPracticeId}
            columns={[
              { key: 'practiceCode', header: t('m35.common.code'), render: (r) => r.practiceCode },
              { key: 'practiceName', header: t('m35.common.name'), render: (r) => r.practiceName },
              { key: 'businessArea', header: t('m35.bp.businessArea'), render: (r) => r.businessArea },
              { key: 'approvalStatus', header: t('m35.bp.approval'), render: (r) => r.approvalStatus },
              { key: 'status', header: t('m35.common.status'), render: (r) => r.status },
              { key: 'actions', header: t('m35.common.actions'), render: (r) => (
                <>
                  {r.approvalStatus === 'PENDING' && (
                    <>
                      <Button variant="secondary" type="button" aria-label={`approve-${r.practiceCode}`} disabled={busy} onClick={() => void act(r, 'approve', t('m35.common.act.approved'), 'M35_BEST_PRACTICE_APPROVE')}>{t('m35.common.btnApprove')}</Button>
                      <input aria-label={`reason-${r.practiceCode}`} value={reasons[r.bestPracticeId] ?? ''} onChange={(e) => setReasons((m) => ({ ...m, [r.bestPracticeId]: e.target.value }))} placeholder={t('m35.common.reason')} />
                      <Button variant="danger" type="button" aria-label={`reject-${r.practiceCode}`} disabled={busy} onClick={() => void act(r, 'reject', t('m35.common.act.rejected'), 'M35_BEST_PRACTICE_APPROVE', { reason: reasons[r.bestPracticeId] ?? '' })}>{t('m35.common.btnReject')}</Button>
                    </>
                  )}
                  {r.approvalStatus === 'APPROVED' && r.status !== 'PUBLISHED' && (
                    <Button variant="primary" type="button" aria-label={`publish-${r.practiceCode}`} disabled={busy} onClick={() => void act(r, 'publish', t('m35.common.act.published'), 'M35_BEST_PRACTICE_APPROVE')}>{t('m35.common.btnPublish')}</Button>
                  )}
                  {r.status !== 'PUBLISHED' && (
                    <Button variant="danger" type="button" aria-label={`delete-${r.practiceCode}`} disabled={busy} onClick={() => void remove(r)}>{t('m35.common.delete')}</Button>
                  )}
                </>
              ) },
            ]}
          />
        </>
      )}
    </main>
  );
}
